import {
  formatCompactDate,
  getEntryDayKey,
  getEntrySearchText,
  getMeaningfulSummary,
  getTherapyPrepCategory,
  groupMedicationEntriesByDose,
  isBehaviorEntry,
  isFoodEntry,
  isImportantMomentEntry,
  isMedicationEntry,
  isSleepEntry,
  normalizeText,
  toEntryDate,
} from "./dailyCareReportEntryUtils";

const DISTURBED_SLEEP_PATTERNS = [
  /restless/i,
  /disturb/i,
  /woke/i,
  /waking/i,
  /broken sleep/i,
  /poor sleep/i,
  /trouble sleeping/i,
  /night waking/i,
  /interrupted/i,
  /short sleep/i,
  /bad night/i,
  /sleep \w*poor/i,
];

const STABLE_SLEEP_PATTERNS = [
  /slept through/i,
  /rested/i,
  /good sleep/i,
  /steady sleep/i,
  /calm night/i,
  /well slept/i,
];

const FOOD_SIGNAL_PATTERNS = [
  /low appetite/i,
  /poor appetite/i,
  /didn'?t eat/i,
  /did not eat/i,
  /skipped (?:breakfast|lunch|dinner|a meal|meals)/i,
  /refused to eat/i,
  /picky/i,
  /selective eating/i,
  /snack(?:s)? only/i,
  /small meal/i,
  /barely ate/i,
  /ate a little/i,
];

const BEHAVIOR_CONCERN_RULES = [
  {
    key: "aggression",
    label: "Aggression",
    patterns: [/aggress/i, /\bhit(?:ting|s|)\b/i, /\bbit(?:e|ing|es)?\b/i, /\bkick(?:ed|ing|s)?\b/i, /\bthrow(?:s|ing|n)?\b/i],
  },
  {
    key: "school_refusal",
    label: "School refusal",
    patterns: [/school refusal/i, /refus(?:ed|al).*school/i, /won'?t go to school/i, /\bschool\b.*\brefus/i],
  },
  {
    key: "escalation",
    label: "Escalation",
    patterns: [/meltdown/i, /tantrum/i, /elop(?:e|ed|ing)?/i, /\bunsafe\b/i, /restraint/i, /panic/i],
  },
];

const SIGNAL_TIER = {
  HIGH: "high_signal",
  MEDIUM: "medium_signal",
  ROUTINE: "routine",
};

const ROUTINE_TITLE_PATTERNS = [
  /^behavior$/,
  /^food$/,
  /^sleep$/,
  /^note$/,
  /^daily log$/,
  /^entry$/,
  /^daily care$/,
];

const PROFILE_CONTEXT_PATTERNS = [
  /\bemergency contacts?\b/i,
  /\bprimary (?:parent|contact)\b/i,
  /\bsecondary contact\b/i,
  /\bmedical information\b/i,
  /\bdiagnosis\b/i,
  /\bcurrent medications?\b/i,
  /\ballerg(?:y|ies)\b/i,
  /\bcommunication style\b/i,
  /\bsensory considerations?\b/i,
  /\bhelpful supports?\b/i,
  /\bfood preferences?\b/i,
  /\bsleep notes?\b/i,
  /\bschool (?:&|and) therapy notes?\b/i,
  /\bbehavior support notes?\b/i,
  /\badditional notes?\b/i,
];

const SENTENCE_BOUNDARY_PATTERN = /(?<=[.!?])\s+/;

const normalizeComparableText = (value = "") =>
  normalizeText(value)
    .toLowerCase()
    .replace(/\b(?:the|a|an|and|or|but|then|later|some|of|on|in|to|for|with|from|after|before|during|was|were|is|are|it|that|this)\b/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const isProfileContextBlob = (entry = {}) => {
  const text = normalizeComparableText([
    getMeaningfulSummary(entry),
    entry.title,
    entry.content,
    entry.originalData?.text,
    entry.originalData?.notes,
  ].filter(Boolean).join(" "));

  if (!text || text.length < 180) {
    return false;
  }

  const matchedSections = PROFILE_CONTEXT_PATTERNS.reduce(
    (count, pattern) => count + (pattern.test(text) ? 1 : 0),
    0
  );

  return matchedSections >= 3;
};

const getKeyEventSummary = (entry = {}) => {
  const summary = normalizeText(getMeaningfulSummary(entry));
  if (!summary) {
    return "";
  }

  if (summary.length <= 260) {
    return summary;
  }

  const sentences = summary.split(SENTENCE_BOUNDARY_PATTERN).filter(Boolean);
  const concise = sentences.slice(0, 2).join(" ").trim();
  if (concise && concise.length <= 320) {
    return concise;
  }

  const clipped = summary.slice(0, 280).trim();
  const lastSpace = clipped.lastIndexOf(" ");
  return `${clipped.slice(0, lastSpace > 160 ? lastSpace : clipped.length).trim()}...`;
};

const getTherapyPrepSignalTier = (entry = {}) => {
  const text = getEntrySearchText(entry);
  const category = getTherapyPrepCategory(entry);

  if (isImportantMomentEntry(entry)) {
    return SIGNAL_TIER.HIGH;
  }

  if (category === "medication") {
    return SIGNAL_TIER.HIGH;
  }

  if (category === "sleep") {
    return DISTURBED_SLEEP_PATTERNS.some((pattern) => pattern.test(text)) ? SIGNAL_TIER.HIGH : SIGNAL_TIER.MEDIUM;
  }

  if (category === "behavior") {
    return BEHAVIOR_CONCERN_RULES.some((rule) => rule.patterns.some((pattern) => pattern.test(text)))
      ? SIGNAL_TIER.HIGH
      : SIGNAL_TIER.MEDIUM;
  }

  if (category === "food") {
    return FOOD_SIGNAL_PATTERNS.some((pattern) => pattern.test(text))
      ? SIGNAL_TIER.HIGH
      : SIGNAL_TIER.MEDIUM;
  }

  if (category === "notes") {
    return normalizeComparableText(getMeaningfulSummary(entry) || entry.title || "").length > 0
      ? SIGNAL_TIER.MEDIUM
      : SIGNAL_TIER.ROUTINE;
  }

  return SIGNAL_TIER.MEDIUM;
};

const isLowInformationTherapyEntry = (entry = {}) => {
  if (isImportantMomentEntry(entry)) {
    return false;
  }

  const summary = normalizeComparableText(getMeaningfulSummary(entry));
  const title = normalizeComparableText(entry.title || entry.categoryLabel || entry.titlePrefix || "");
  const category = normalizeComparableText(getTherapyPrepCategory(entry));
  const content = normalizeComparableText(entry.content || entry.notes || entry.description || entry.text || "");
  const titleMatchesCategory = Boolean(title && category && title === category);
  const genericTitle = ROUTINE_TITLE_PATTERNS.some((pattern) => pattern.test(title));

  return (
    (!summary || summary.length < 18)
    && (genericTitle || titleMatchesCategory || content.length < 18)
  );
};

const dedupeTherapyPrepEntries = (entries = []) => {
  const seen = new Set();

  return entries.filter((entry) => {
    const key = [
      getEntryDayKey(entry),
      getTherapyPrepCategory(entry),
      normalizeComparableText(getMeaningfulSummary(entry) || entry.title || entry.content || ""),
    ].join("|");

    if (!key || seen.has(key)) {
      return false;
    }

    seen.add(key);
    return true;
  });
};

export const formatSignalTierLabel = (signalTier = SIGNAL_TIER.MEDIUM) => {
  if (signalTier === SIGNAL_TIER.HIGH) {
    return "More concerning item";
  }

  if (signalTier === SIGNAL_TIER.ROUTINE) {
    return "Routine";
  }

  return "Moderate concern";
};

const createConcernItem = (label, text, signalTier, entryIds = []) => ({
  label,
  text,
  signalTier,
  entryIds,
});

const createKeyEventItem = ({ id, label, text, date, category, signalTier, entryIds = [] }) => ({
  id,
  label,
  text,
  date,
  category,
  signalTier,
  entryIds,
});

export const summarizeMedicationEntries = (entries = []) => {
  const medicationEntries = entries.filter(isMedicationEntry);
  if (!medicationEntries.length) {
    return {
      text: "Medication logs were not added for this period.",
      groups: [],
      dayCount: 0,
      entryCount: 0,
    };
  }

  const grouped = medicationEntries.reduce((acc, entry) => {
    const name = normalizeText(
      entry.medicationName
      || entry.medicationDetails?.medicationName
      || entry.medicationDetails?.name
      || entry.title
      || entry.categoryLabel
      || entry.titlePrefix
      || "Medication"
    ) || "Medication";
    const dose = normalizeText([
      entry.medicationScheduleDose || entry.medicationDetails?.dosage || entry.medicationDetails?.dose || entry.dosage || entry.dose,
      entry.medicationScheduleUnit || entry.medicationDetails?.unit || entry.unit,
    ].filter(Boolean).join(" "));
    const status = String(entry.status || entry.backfillStatus || "").toLowerCase() === "missed" ? "missed" : "given";
    const key = `${name.toLowerCase()}|${dose.toLowerCase()}`;

    if (!acc[key]) {
      acc[key] = {
        name,
        dose,
        givenCount: 0,
        missedCount: 0,
        dayKeys: new Set(),
      };
    }

    acc[key][status === "missed" ? "missedCount" : "givenCount"] += 1;
    acc[key].dayKeys.add(getEntryDayKey(entry));
    return acc;
  }, {});

  const groups = Object.values(grouped)
    .sort((a, b) => (b.givenCount + b.missedCount) - (a.givenCount + a.missedCount) || a.name.localeCompare(b.name))
    .slice(0, 3);

  const totalDays = new Set(medicationEntries.map(getEntryDayKey)).size;
  const groupText = groups.map((group) => {
    const label = group.dose ? `${group.name} ${group.dose}` : group.name;
    if (group.givenCount && group.missedCount) {
      return `${label} — given ${group.givenCount} ${group.givenCount === 1 ? "time" : "times"}, missed ${group.missedCount} ${group.missedCount === 1 ? "time" : "times"}`;
    }
    if (group.missedCount && !group.givenCount) {
      return `${label} — missed ${group.missedCount} ${group.missedCount === 1 ? "time" : "times"}`;
    }
    const count = group.givenCount || group.missedCount;
    return `${label} — given ${count} ${count === 1 ? "time" : "times"}`;
  }).join("; ");
  const extraCount = Object.keys(grouped).length - groups.length;
  const primaryLabel = groups.length === 1 && groups[0]
    ? (groups[0].dose ? `${groups[0].name} ${groups[0].dose}` : groups[0].name)
    : "Medication notes";

  return {
    text: groups.length === 1 && totalDays === 1
      ? `${primaryLabel} was mentioned once this week.`
      : groups.length === 1
        ? `${primaryLabel} was mentioned on ${totalDays} ${totalDays === 1 ? "day" : "days"} this week.`
        : `Medication notes were mentioned on ${totalDays} ${totalDays === 1 ? "day" : "days"} this week${groupText ? `: ${groupText}` : ""}${extraCount > 0 ? `; +${extraCount} more` : ""}.`,
    groups,
    dayCount: totalDays,
    entryCount: medicationEntries.length,
  };
};

export const summarizeSleepEntries = (entries = []) => {
  const sleepEntries = dedupeTherapyPrepEntries(entries.filter(isSleepEntry));
  if (!sleepEntries.length) {
    return {
      text: "Sleep logs were not added for this period.",
      dayKeys: new Set(),
      entryCount: 0,
    };
  }

  const dayKeys = new Set(sleepEntries.map(getEntryDayKey));
  const restlessCount = sleepEntries.filter((entry) => DISTURBED_SLEEP_PATTERNS.some((pattern) => pattern.test(getEntrySearchText(entry)))).length;
  const steadyCount = sleepEntries.filter((entry) => STABLE_SLEEP_PATTERNS.some((pattern) => pattern.test(getEntrySearchText(entry)))).length;

  let patternText = "Sleep notes looked mostly steady.";
  if (restlessCount && steadyCount) {
    patternText = "Sleep notes were mixed, with some restless or interrupted nights.";
  } else if (restlessCount) {
    patternText = "Sleep notes mentioned restless or interrupted sleep on several nights.";
  }

  return {
    text: `Sleep notes were mentioned on ${dayKeys.size} ${dayKeys.size === 1 ? "day" : "days"}. ${patternText}`,
    dayKeys,
    entryCount: sleepEntries.length,
  };
};

export const summarizeFoodEntries = (entries = []) => {
  const foodEntries = dedupeTherapyPrepEntries(entries.filter(isFoodEntry));
  if (!foodEntries.length) {
    const generalEntries = entries.filter((entry) => !isSleepEntry(entry) && !isMedicationEntry(entry) && !isBehaviorEntry(entry));
    return {
      text: generalEntries.length
        ? "General notes were brief and mostly routine."
        : "Food and general notes were limited for this period.",
      dayKeys: new Set(generalEntries.map(getEntryDayKey)),
      entryCount: generalEntries.length,
    };
  }

  const dayKeys = new Set(foodEntries.map(getEntryDayKey));
  const signalCount = foodEntries.filter((entry) => FOOD_SIGNAL_PATTERNS.some((pattern) => pattern.test(getEntrySearchText(entry)))).length;
  const noteText = signalCount
    ? `Food-related notes mentioned lower appetite or selective eating on ${dayKeys.size} ${dayKeys.size === 1 ? "day" : "days"}.`
    : "Food-related notes were mostly routine.";

  return {
    text: noteText,
    dayKeys,
    entryCount: foodEntries.length,
  };
};

export const summarizeBehaviorEntries = (entries = []) => {
  const behaviorEntries = dedupeTherapyPrepEntries(entries.filter(isBehaviorEntry)).filter((entry) => !isLowInformationTherapyEntry(entry));
  if (!behaviorEntries.length) {
    return {
      text: "Behavior was not logged in this period.",
      concerns: [],
      dayKeys: new Set(),
      entryCount: 0,
    };
  }

  const buckets = behaviorEntries.reduce((acc, entry) => {
    const text = getEntrySearchText(entry);
    const matchedRule = BEHAVIOR_CONCERN_RULES.find((rule) => rule.patterns.some((pattern) => pattern.test(text)));
    const key = matchedRule?.key || "general_behavior";
    if (!acc[key]) {
      acc[key] = {
        label: matchedRule?.label || (isImportantMomentEntry(entry) ? "Important moment" : "Behavior concern"),
        count: 0,
        dayKeys: new Set(),
        examples: [],
        entryIds: [],
        signalTier: matchedRule ? SIGNAL_TIER.HIGH : SIGNAL_TIER.MEDIUM,
      };
    }

    acc[key].count += 1;
    acc[key].dayKeys.add(getEntryDayKey(entry));
    acc[key].entryIds.push(entry.id);
    const example = getMeaningfulSummary(entry);
    if (example && acc[key].examples.length < 2) {
      acc[key].examples.push(example);
    }
    return acc;
  }, {});

  const concerns = Object.values(buckets)
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label))
    .slice(0, 3)
    .map((bucket) => {
      const dayCount = bucket.dayKeys.size;
      const exampleText = bucket.examples[0] ? ` Example: ${bucket.examples[0]}.` : "";
      return createConcernItem(
        bucket.label,
        `${bucket.label} was mentioned ${bucket.count} ${bucket.count === 1 ? "time" : "times"} on ${dayCount} ${dayCount === 1 ? "day" : "days"}.${exampleText}`,
        bucket.signalTier,
        bucket.entryIds
      );
    });

  return {
    text: `${behaviorEntries.length} behavior ${behaviorEntries.length === 1 ? "entry was" : "entries were"} logged.`,
    concerns,
    dayKeys: new Set(behaviorEntries.map(getEntryDayKey)),
    entryCount: behaviorEntries.length,
  };
};

export const summarizePatterns = ({ sleepSummary, behaviorSummary, foodSummary, medicationSummary }) => {
  const patterns = [];
  const sleepBehaviorOverlap = [...sleepSummary.dayKeys].filter((dayKey) => behaviorSummary.dayKeys.has(dayKey));
  const foodBehaviorOverlap = [...foodSummary.dayKeys].filter((dayKey) => behaviorSummary.dayKeys.has(dayKey));

  if (sleepSummary.entryCount && behaviorSummary.entryCount && sleepBehaviorOverlap.length) {
    patterns.push("Sleep concerns and behavior notes appeared together on some of the same days.");
  }

  if (foodSummary.entryCount && behaviorSummary.entryCount && foodBehaviorOverlap.length) {
    patterns.push("Food notes and behavior notes happened on some of the same days.");
  }

  if (medicationSummary.entryCount) {
    const totalGroups = medicationSummary.groups?.length || 0;
    if (totalGroups === 1 && medicationSummary.dayCount >= 3) {
      patterns.push("Medication notes were fairly steady across the period.");
    } else if (medicationSummary.dayCount >= 4) {
      patterns.push("Medication notes were mentioned on most days in this period.");
    }
  }

  if (!patterns.length) {
    patterns.push("Nothing clear stood out from this period.");
  }

  return patterns.slice(0, 3);
};

export const buildWeeklySummary = ({
  sleepSummary,
  behaviorSummary,
  foodSummary,
  medicationSummary,
  notableConcerns = [],
}) => {
  const lines = [];
  const concernsText = notableConcerns.length
    ? `A few items may be worth discussing, especially ${notableConcerns.slice(0, 2).map((item) => item.label || item.text || item).join(" and ")}.`
    : "Nothing major stood out, and most notes looked routine.";

  lines.push("Based on this week's notes,");

  if (sleepSummary.entryCount && behaviorSummary.entryCount) {
    lines.push("Sleep concerns and behavior notes appeared together on some of the same days.");
  } else if (sleepSummary.entryCount) {
    lines.push("Sleep was mentioned several times, so it may be worth checking what seemed to help on the better nights.");
  }

  if (foodSummary.entryCount) {
    lines.push("Food notes were mostly brief, with a few meals or snacks that may be worth discussing if appetite is a concern.");
  }

  if (medicationSummary.entryCount) {
    lines.push("Medication notes were present across the period, which may be helpful to review alongside the session notes.");
  }

  lines.push(concernsText);

  return lines.slice(0, 3).join(" ");
};

export const buildTherapyPrepSummary = (entries = []) => {
  const sleepSummary = summarizeSleepEntries(entries);
  const behaviorSummary = summarizeBehaviorEntries(entries);
  const medicationSummary = summarizeMedicationEntries(entries);
  const foodSummary = summarizeFoodEntries(entries);
  const notableConcerns = behaviorSummary.concerns
    .filter((item) => item.signalTier !== SIGNAL_TIER.ROUTINE)
    .slice(0, 3);
  const patterns = summarizePatterns({
    sleepSummary,
    behaviorSummary,
    foodSummary,
    medicationSummary,
  });
  const weeklySummary = buildWeeklySummary({
    sleepSummary,
    behaviorSummary,
    foodSummary,
    medicationSummary,
    notableConcerns,
  });

  return {
    bullets: [
      { label: "Sleep", text: sleepSummary.text },
      { label: "Behavior", text: behaviorSummary.text },
      { label: "Medication", text: medicationSummary.text },
      { label: "Food", text: foodSummary.text },
    ],
    weeklySummary,
    notableConcerns,
    possiblePatterns: patterns,
    medicationSummary,
  };
};

export const buildQuestionsToDiscuss = (summaryData) => {
  if (Array.isArray(summaryData?.questionsToDiscuss) && summaryData.questionsToDiscuss.length) {
    return summaryData.questionsToDiscuss
      .map((item) => {
        if (typeof item === "string") {
          const question = item.trim();
          return question ? { question, why: "", sourceEntryIds: [] } : null;
        }

        const question = typeof item?.question === "string" ? item.question.trim() : "";
        if (!question) {
          return null;
        }

        return {
          question,
          why: typeof item?.why === "string" ? item.why.trim() : "",
          sourceEntryIds: Array.isArray(item?.sourceEntryIds)
            ? item.sourceEntryIds.filter((id) => typeof id === "string" && id.trim()).map((id) => id.trim())
            : [],
        };
      })
      .filter(Boolean)
      .slice(0, 5);
  }

  const questions = [];
  const bullets = Array.isArray(summaryData?.bullets) ? summaryData.bullets : [];
  const sleepText = bullets.find((item) => item.label === "Sleep")?.text || "";
  const behaviorConcerns = summaryData.notableConcerns || [];
  const foodText = bullets.find((item) => item.label === "Food")?.text || "";
  const medicationText = bullets.find((item) => item.label === "Medication")?.text || "";

  if (/restless|interrupted|mixed/i.test(sleepText)) {
    questions.push("What seemed to line up with the restless sleep?");
  } else {
    questions.push("How has sleep been feeling during this period?");
  }

  if (behaviorConcerns.length) {
    questions.push("What seemed to help when these behavior concerns came up?");
  } else {
    questions.push("Were there any new behavior triggers or changes you’d want the care team to know about?");
  }

  if (/medication/i.test(medicationText)) {
    questions.push("Any medication timing changes, missed doses, or side effects to note?");
  } else if (/food-related notes mentioned/i.test(foodText)) {
    questions.push("Did appetite or food preferences seem different on the harder days?");
  } else {
    questions.push("Is there anything else you’d like the care team to keep in mind?");
  }

  return questions.slice(0, 3).map((question) => ({ question, why: "", sourceEntryIds: [] }));
};

export const buildKeyEvents = (entries = []) => {
  const sorted = [...dedupeTherapyPrepEntries(entries)].sort((a, b) => toEntryDate(b.timestamp) - toEntryDate(a.timestamp));
  const behaviorEvents = sorted.filter((entry) => (
    (isBehaviorEntry(entry) || isImportantMomentEntry(entry))
    && getTherapyPrepSignalTier(entry) !== SIGNAL_TIER.ROUTINE
    && !isLowInformationTherapyEntry(entry)
    && !isProfileContextBlob(entry)
  ));
  const medicationEvents = sorted.filter((entry) => isMedicationEntry(entry) && getTherapyPrepSignalTier(entry) !== SIGNAL_TIER.ROUTINE);
  const events = [];

  behaviorEvents.slice(0, 4).forEach((entry) => {
    const signalTier = getTherapyPrepSignalTier(entry);
    const summary = getKeyEventSummary(entry);
    events.push(createKeyEventItem({
      id: entry.id,
      label: isImportantMomentEntry(entry) ? "Important moment" : "Behavior",
      text: summary || "Behavior note",
      date: toEntryDate(entry.timestamp),
      category: getTherapyPrepCategory(entry),
      signalTier,
      entryIds: [entry.id],
    }));
  });

  if (events.length < 4 && medicationEvents.length) {
    const medSummary = summarizeMedicationEntries(medicationEvents);
    medSummary.groups.slice(0, 2).forEach((group, index) => {
      events.push(createKeyEventItem({
        id: `med-${index}-${group.name}`,
        label: "Medication",
        text: group.dose ? `${group.name} ${group.dose} — given ${group.givenCount || group.missedCount} times` : `${group.name} — given ${group.givenCount || group.missedCount} times`,
        date: toEntryDate(medicationEvents[0].timestamp),
        category: "medication",
        signalTier: SIGNAL_TIER.HIGH,
        entryIds: medicationEvents.map((item) => item.id),
      }));
    });
  }

  return events.slice(0, 5);
};

export const buildShareText = ({ childName, periodLabel, dateSections, summaryData, questions, keyEvents }) => {
  const lines = [`Visit Prep — ${childName}`, periodLabel, ""];

  lines.push("Summary");
  summaryData.bullets.forEach((bullet) => {
    lines.push(`- ${bullet.label}: ${bullet.text}`);
  });

  if (summaryData.notableConcerns.length) {
    lines.push("");
    lines.push("Notable concerns");
    summaryData.notableConcerns.forEach((item) => {
      lines.push(`- ${formatSignalTierLabel(item.signalTier)}: ${item.text || item}`);
    });
  }

  if (summaryData.possiblePatterns.length) {
    lines.push("");
    lines.push("What seemed to line up");
    summaryData.possiblePatterns.forEach((item) => {
      lines.push(`- ${item}`);
    });
  }

  if (questions.length) {
    lines.push("");
    lines.push("Questions to discuss");
    questions.forEach((item) => {
      const question = typeof item === "string" ? item : item?.question;
      const why = typeof item === "object" && item?.why ? ` (${item.why})` : "";
      if (question) {
        lines.push(`- ${question}${why}`);
      }
    });
  }

  if (keyEvents.length) {
    lines.push("");
    lines.push("Key events");
    keyEvents.forEach((item) => {
      lines.push(`- ${formatCompactDate(item.date)} · ${item.label}: ${item.text}`);
    });
  }

  lines.push("");
  lines.push("Full logs");
  lines.push("");

  dateSections.forEach((section) => {
    lines.push(section.date.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" }));
    lines.push("───────────────────");
    const meds = groupMedicationEntriesByDose(section.items.filter(isMedicationEntry));
    const otherItems = section.items.filter((entry) => !isMedicationEntry(entry));

    meds.forEach((group) => {
      const label = group.dose ? `${group.name} ${group.dose}` : group.name;
      const summary = group.givenCount && group.missedCount
        ? `given ${group.givenCount} times, missed ${group.missedCount} times`
        : `given ${group.givenCount || group.missedCount} times`;
      lines.push(`Medication · ${label} — ${summary}`);
    });

    otherItems.forEach((entry) => {
      lines.push(`${getTherapyPrepCategory(entry) === "behavior" ? "BEHAVIOR" : getTherapyPrepCategory(entry) === "sleep" ? "SLEEP" : getTherapyPrepCategory(entry) === "food" ? "FOOD" : getTherapyPrepCategory(entry) === "medication" ? "MEDICATION" : "DAILY LOG"} · ${toEntryDate(entry.timestamp).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })} · ${getMeaningfulSummary(entry)}`);
    });
    lines.push("");
  });

  return lines.join("\n").trim();
};
